import { useEffect, useState } from 'react';
import { Button, Table, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { Stack, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Box } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import Swal from 'sweetalert2';
import {
  DOCUMENT_ACCEPT, DOCUMENT_MAX_MO, deleteKycDocument, fetchKycDocuments, isFileTooLarge, uploadKycDocument,
} from './kycDocumentsApi';
import type { DemandeKycDocumentRow } from '../../../Models/DemandeKyc';

interface KycDocumentsPanelProps {
  idDemande: number | string;
  uploadePar: string;
}

const MIME_IMAGE = /^image\//;
const MIME_PDF = /pdf/i;

function documentDataUri(doc: DemandeKycDocumentRow): string {
  return `data:${doc.TypeMime || 'application/octet-stream'};base64,${doc.ContenuFichier}`;
}

// Pièces jointes d'une demande KYC — réutilisé à la fois juste après la
// création d'une demande (Modification/Relation) et lors de son traitement
// (Validation), puisque le backend accepte l'ajout tant que la demande est
// en attente, peu importe l'écran d'où on la consulte.
export default function KycDocumentsPanel({ idDemande, uploadePar }: KycDocumentsPanelProps) {
  const [documents, setDocuments] = useState<DemandeKycDocumentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<DemandeKycDocumentRow | null>(null);

  async function loadDocuments() {
    setLoading(true);
    try {
      setDocuments(await fetchKycDocuments(idDemande));
    } catch {
      setDocuments([]);
      Swal.fire({ title: 'Erreur', text: 'Impossible de récupérer les pièces jointes.', icon: 'error' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDocuments(); }, [idDemande]);

  async function handleUpload(file: File) {
    if (isFileTooLarge(file)) {
      Swal.fire({ title: 'Fichier trop volumineux', text: `La taille maximale est de ${DOCUMENT_MAX_MO} Mo.`, icon: 'warning' });
      return;
    }
    setUploading(true);
    try {
      const result = await uploadKycDocument(idDemande, uploadePar, file);
      if (result.ok) {
        await loadDocuments();
      } else {
        Swal.fire({ title: 'Erreur', text: result.message, icon: 'error' });
      }
    } finally {
      setUploading(false);
    }
  }

  async function removeDocument(doc: DemandeKycDocumentRow) {
    // Action indépendante de la décision Valider/Rejeter de la demande — on ne
    // supprime ici que le fichier joint, jamais la demande elle-même.
    const confirmation = await Swal.fire({
      title: 'Supprimer ce fichier',
      text: `Voulez-vous vraiment supprimer "${doc.NomFichierOriginal}" ? Cela ne concerne que ce fichier, pas la demande elle-même.`,
      showCancelButton: true,
      confirmButtonText: 'Oui',
      confirmButtonColor: 'darkred',
      cancelButtonColor: 'grey',
      cancelButtonText: 'Non',
      icon: 'question',
    });
    if (!confirmation.isConfirmed) return;

    const result = await deleteKycDocument(doc.IdDocument);
    if (result.ok) {
      await loadDocuments();
    } else {
      Swal.fire({ title: 'Erreur', text: result.message, icon: 'error' });
    }
  }

  const dataUri = previewDoc ? documentDataUri(previewDoc) : '';
  const estImage = previewDoc ? MIME_IMAGE.test(previewDoc.TypeMime || '') : false;
  const estPdf = previewDoc ? MIME_PDF.test(previewDoc.TypeMime || '') || /\.pdf$/i.test(previewDoc.Extension || '') : false;

  return (
    <div>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AttachFileIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase' }}>
            Pièces jointes
          </Typography>
        </Stack>
        <Upload
          accept={DOCUMENT_ACCEPT}
          showUploadList={false}
          disabled={uploading}
          beforeUpload={((file: File) => { handleUpload(file); return false; }) as UploadProps['beforeUpload']}
        >
          <Button
            size="small" className="bt_add" style={{ color: '#fff' }} loading={uploading}
            icon={<UploadFileIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />}
          >
            Ajouter un document
          </Button>
        </Upload>
      </Stack>
      <Table
        rowKey="IdDocument" size="small" bordered pagination={false}
        loading={loading}
        dataSource={documents}
        locale={{ emptyText: 'Aucun document joint.' }}
        columns={[
          { title: 'Fichier', dataIndex: 'NomFichierOriginal', key: 'NomFichierOriginal' },
          { title: 'Taille', dataIndex: 'TailleOctets', key: 'TailleOctets', render: (v: number) => v ? `${Math.round(v / 1024)} Ko` : '—' },
          { title: 'Ajouté par', dataIndex: 'UploadePar', key: 'UploadePar' },
          { title: 'Date', dataIndex: 'DateUpload', key: 'DateUpload' },
          {
            title: 'Action', key: 'action', align: 'right' as const,
            render: (_: any, doc: DemandeKycDocumentRow) => (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" icon={<VisibilityIcon style={{ fontSize: 14 }} />} onClick={() => setPreviewDoc(doc)}>
                  Voir
                </Button>
                <Button size="small" icon={<DeleteOutlineIcon style={{ fontSize: 14 }} />} onClick={() => removeDocument(doc)}>
                  Supprimer
                </Button>
              </Stack>
            ),
          },
        ] as any}
      />

      <Dialog open={Boolean(previewDoc)} onClose={() => setPreviewDoc(null)} maxWidth="md" fullWidth>
        <DialogTitle>{previewDoc?.NomFichierOriginal}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'grey.100', minHeight: 300 }}>
          {estImage && (
            <Box component="img" src={dataUri} alt={previewDoc?.NomFichierOriginal} sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          )}
          {estPdf && (
            <Box component="iframe" src={dataUri} title={previewDoc?.NomFichierOriginal} sx={{ width: '100%', height: '70vh', border: 'none' }} />
          )}
          {!estImage && !estPdf && (
            <Typography sx={{ alignSelf: 'center', textAlign: 'center' }} color="text.secondary">
              Aperçu non disponible pour ce type de fichier — téléchargez-le pour l'ouvrir.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            icon={<DownloadIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />}
            href={dataUri}
            download={previewDoc?.NomFichierOriginal}
            className="bt_add"
            style={{ color: '#fff' }}
          >
            Télécharger
          </Button>
          <Button onClick={() => setPreviewDoc(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
